import { BxfChatBubble, BxfChatBubbleType } from '@blk/bxf-react-components';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';

import {
    ActionButtonRenderer,
    CaptureFeedback,
    MarkdownRenderer,
    RelationshipManagerContacts,
    SourceList,
    TableRenderer,
} from '../components';
import {
    CHAT_BUBBLE_DATA_LINK_EVENTS,
    ServerMessageConstants,
} from '../constants';
import { useServerMessageEvents } from '../hooks';
import {
    ChatBlkQuery,
    ContentBlock,
    OutputSection,
    QueryState,
} from '../types';
import { getKey } from '../utils';

const { DATA_CY_SERVER_CHAT_BUBBLE } = ServerMessageConstants;

const ServerResponseChatBubble = styled.div`
    padding: 16px 0; // $space-s
`;
const ServerResponseContent = styled.div`
    font-size: 16px; // $space-s
    line-height: 24px; // $space-m
    letter-spacing: 0;
    font-weight: 400;

    .server-message-response-content--paragraph {
        margin-inline-end: 0px !important;
        margin-inline-start: 0px !important;
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--unordered-list {
        list-style-type: disc !important;
        padding-inline-start: 40px !important; // $space-xl
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--ordered-list {
        list-style-type: decimal !important;
        padding-inline-start: 40px !important; // $space-xl
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-1 {
        // oneds-blk-heading-m:
        font-size: clamp(1.4469rem, 1.2383rem + 0.9269vw, 2.0725rem);
        line-height: 1.24;
        word-wrap: break-word;
        font-weight: 800;
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-2 {
        // oneds-blk-heading-s:
        font-size: clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);
        line-height: 1.24;
        font-weight: 800;
        word-wrap: break-word;
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-3 {
        // oneds-blk-heading-xs:
        font-size: clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);
        line-height: 1.24;
        word-wrap: break-word;
        font-weight: 800;
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-4 {
        // oneds-blk-heading-xxs:
        font-size: clamp(1rem, 1rem + 0vw, 1rem);
        line-height: 1.25;
        word-wrap: break-word;
        font-weight: 800;
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-5 {
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--header-6 {
        margin-block-end: 16px !important; // $space-s
    }
    .server-message-response-content--pdf-link {
        display: inline-flex;
        margin-left: -6px;
        padding-right: 1rem;
    }
`;

interface ServerMessageProps {
    query: ChatBlkQuery;
    onImgLoad?: () => void;
}

/**
 * Component that renders a server message within a chat bubble.
 *
 * @param {ServerMessageProps} props - The properties for the ServerMessage component.
 * @param {Query} props.query - The query object containing the state, sources, id, response, and chatBlkEventId.
 * @param {Function} props.onImgLoad - Callback function to handle image load events.
 *
 * @returns {JSX.Element} The rendered server message component.
 *
 * The component uses the `useServerMessageEvents` hook to manage various event handlers and state.
 * It conditionally renders a `ServerResponseChatBubble` containing a `BxfChatBubble` with various event handlers and properties.
 * Inside the chat bubble, it renders a `MarkdownRenderer`, `CaptureFeedback`, and `SourceList` components.
 */
const ServerMessage = ({
    query,
    onImgLoad,
}: ServerMessageProps): JSX.Element => {
    const {
        state,
        sources = [],
        id,
        response = '',
        output,
        hashId,
    } = query || {};

    const {
        chatBubbleResponseRef,
        hasSources,
        hideCopyButton,
        followUpQuestions,
        similarQuestions,
        isRequiredFeedbackSubmitted,
        hasCustomFeedback,
        hasFooter,
        handleProdFeedbackSubmitted,
        handleFeedbackIconToggle,
        handleFeedbackToggle,
        handleCopyButtonClicked,
        handleFollowUpQuestionClicked,
        handleSourcesAccordionToggled,
        handleSimilarQuestionClicked,
    } = useServerMessageEvents(query);

    const [pdfArray, setPdfArray] = useState<number[]>([]);
    const outputSections: Array<OutputSection> = output?.sections ?? [];
    useEffect(() => {
        if (outputSections.length) {
            setPdfArray(sectionPdfIndexes(outputSections));
        }
    }, [outputSections]);

    const sectionPdfIndexes = (sections: Array<OutputSection>): number[] => {
        const indexes: number[] = [];

        sections.forEach((section: OutputSection) => {
            const contentBlocks: Array<ContentBlock> =
                section?.contentBlocks ?? [];

            contentBlocks.forEach((block: ContentBlock, blockIndex: number) => {
                const { textBlock } = block;
                if (textBlock?.content) {
                    // Matches plain .pdf endings or markdown links ending in .pdf
                    if (/\.pdf\)?$/i.test(textBlock.content.trim())) {
                        indexes.push(blockIndex);
                    }
                }
            });
        });

        // Determine that numbers in indexes are consecutive
        const consecutiveIndexes =
            indexes.length > 1 &&
            indexes.every((value, index, array) => {
                if (index === 0) return true;
                return value === array[index - 1] + 1;
            });

        return consecutiveIndexes ? indexes : [];
    };

    const renderSections = (sections: Array<OutputSection>) => {
        return sections?.map((section: OutputSection, sectionIndex) => {
            const { contentBlocks }: { contentBlocks: Array<ContentBlock> } =
                section;

            return contentBlocks?.map((block: ContentBlock, blockIndex) => {
                const {
                    description = '',
                    textBlock,
                    propertiesBlock,
                    buttonsBlock,
                } = block;

                if (textBlock?.content) {
                    // Only render the PDF block once, at the first occurrence in pdfArray
                    if (pdfArray.includes(blockIndex)) {
                        // Only render for the first PDF index
                        if (pdfArray[0] === blockIndex) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        marginTop: '16px',
                                    }}
                                    key={getKey(
                                        `pdf-div-${id}`,
                                        sectionIndex + blockIndex
                                    )}
                                >
                                    {pdfArray.map((pdfIndex) => (
                                        <MarkdownRenderer
                                            id={id}
                                            key={getKey(
                                                `$text-${id}`,
                                                sectionIndex + pdfIndex
                                            )}
                                            sources={sources}
                                            response={
                                                contentBlocks[pdfIndex]
                                                    ?.textBlock?.content
                                            }
                                            onImgLoad={onImgLoad}
                                        />
                                    ))}
                                </div>
                            );
                        }
                        // Skip rendering for other pdfArray indexes
                        return null;
                    }
                    // Render normal MarkdownRenderer for non-PDF blocks
                    return (
                        <MarkdownRenderer
                            id={id}
                            key={getKey(
                                `$text-${id}`,
                                sectionIndex + blockIndex
                            )}
                            sources={sources}
                            response={textBlock.content}
                            onImgLoad={onImgLoad}
                        />
                    );
                }
                if (propertiesBlock) {
                    return (
                        <TableRenderer
                            key={getKey(
                                `$table-${id}`,
                                sectionIndex + blockIndex
                            )}
                            description={description}
                            {...propertiesBlock}
                        />
                    );
                }
                if (buttonsBlock) {
                    return (
                        <ActionButtonRenderer
                            key={getKey(
                                `$buttons-${id}`,
                                sectionIndex + blockIndex
                            )}
                            {...buttonsBlock}
                        />
                    );
                }
            });
        });
    };

    const renderResponse = (sections: Array<OutputSection>) => {
        if (sections.length) {
            // Prerender check to see how many PDF links are present
            sectionPdfIndexes(sections);
            return [renderSections(sections)];
        }
        return [
            <MarkdownRenderer
                id={id}
                key={getKey(`$text-${id}`, 0)}
                sources={sources}
                response={response}
                onImgLoad={onImgLoad}
            />,
        ];
    };

    return hashId && state !== QueryState.CANCELED ? (
        <ServerResponseChatBubble>
            <BxfChatBubble
                ref={chatBubbleResponseRef}
                key={hashId}
                type={
                    state === QueryState.LOADING
                        ? ('active-response' as BxfChatBubbleType)
                        : ('response' as BxfChatBubbleType)
                }
                data-cy={DATA_CY_SERVER_CHAT_BUBBLE}
                dataLinkEvent={CHAT_BUBBLE_DATA_LINK_EVENTS}
                hideCopyButton={hideCopyButton}
                hasFooter={hasFooter}
                hasSources={hasSources}
                hasCustomFeedback={hasCustomFeedback}
                isFeedbackDisabled={isRequiredFeedbackSubmitted}
                isRequiredFeedbackSubmitted={isRequiredFeedbackSubmitted}
                similarQuestions={similarQuestions}
                followUpQuestions={followUpQuestions}
                onFeedbackSubmitted={handleFeedbackIconToggle}
                onFeedbackToggled={handleFeedbackToggle}
                onAdditionalFeedbackSubmitted={handleProdFeedbackSubmitted}
                onSourcesAccordionToggled={handleSourcesAccordionToggled}
                onFollowUpQuestionClicked={handleFollowUpQuestionClicked}
                onSimilarQuestionClicked={handleSimilarQuestionClicked}
                onCopyButtonClicked={handleCopyButtonClicked}
            >
                <ServerResponseContent slot='content'>
                    <div key={id}>
                        {renderResponse(outputSections)}
                        {output?.metadata?.relationshipManagersIncluded && (
                            <RelationshipManagerContacts />
                        )}
                    </div>
                </ServerResponseContent>
                {state === QueryState.LOADED && <CaptureFeedback />}
                <SourceList sources={sources} />
            </BxfChatBubble>
        </ServerResponseChatBubble>
    ) : (
        <></>
    );
};

export default ServerMessage;
